import React, { useState, useMemo } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  Box,
  Typography,
  Modal,
  Button,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Stack,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Paper,
  Avatar,
  Fade,
  LinearProgress,
  Backdrop,
  Tooltip,
  Badge,
  keyframes, // ADDED: Import keyframes
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as ApproveIcon,
  Cancel as DeclineIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Share as ShareIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Tag as TagIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Favorite as FavoriteIcon,
  Comment as CommentIcon,
  ShareOutlined as ShareOutlinedIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  Security as SecurityIcon,
  PlayArrow as PlayArrowIcon,
  Fullscreen as FullscreenIcon,
  Instagram,
  Facebook,
  Twitter,
  LinkedIn,
  YouTube,
} from "@mui/icons-material";
// ADDED: Import API functions
import { approvePost, rejectPost } from "../../Action/approvalApi";
import { getAuthTokenPayload } from "../../Utils";

// Enable dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Premium animations
const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(59, 130, 246, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.6);
  }
  100% {
    box-shadow: 0 0 5px rgba(59, 130, 246, 0.3);
  }
`;

const slideInUp = keyframes`
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const PostPreviewModal = ({
  open,
  onClose,
  post,
  onPostUpdated,
  userPermissions,
  isApprovalTimeValid,
  getTimeValidationMessage,
}) => {
  const [loading, setLoading] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showFullImage, setShowFullImage] = useState(false);

  // FIXED: Move useMemo hooks before early return
  // ADDED: Check if approval time is valid (memoized to avoid recalculation)
  const canApprove = useMemo(() => {
    if (!post?.rawScheduleDateTime) return false;

    // If functions are passed from parent, use them
    if (isApprovalTimeValid) {
      return isApprovalTimeValid(post.rawScheduleDateTime);
    }

    // Fallback: Local validation
    const now = dayjs().tz(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const scheduledTime = dayjs(post.rawScheduleDateTime).tz(
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );
    const minValidTime = now.add(10, "minute");

    return scheduledTime.isAfter(minValidTime);
  }, [post?.rawScheduleDateTime, isApprovalTimeValid, post]);

  // ADDED: Get validation message
  const validationMessage = useMemo(() => {
    if (!post?.rawScheduleDateTime) return "";

    // If function is passed from parent, use it
    if (getTimeValidationMessage) {
      return getTimeValidationMessage(post.rawScheduleDateTime);
    }

    // Fallback: Local message generation
    const now = dayjs().tz(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const scheduledTime = dayjs(post.rawScheduleDateTime).tz(
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );
    const diffMinutes = scheduledTime.diff(now, "minute");

    if (diffMinutes < 0) {
      return "This post is scheduled in the past and cannot be approved.";
    } else if (diffMinutes < 10) {
      return `This post is scheduled in ${diffMinutes} minute(s). Minimum 10 minutes buffer required.`;
    }
    return "";
  }, [post?.rawScheduleDateTime, getTimeValidationMessage, post]);

  // Get all media URLs from the post
  const mediaUrls = useMemo(() => {
    const urls = [];
    if (post?.mediaUrls && Array.isArray(post.mediaUrls)) {
      urls.push(...post.mediaUrls);
    }
    if (post?.mediaFiles && Array.isArray(post.mediaFiles)) {
      post.mediaFiles.forEach((file) => {
        if (file.s3Url) {
          urls.push(file.s3Url);
        }
      });
    }
    return [...new Set(urls)];
  }, [post]);

  // FIXED: Now early return comes after all hooks
  if (!post) return null;

  const isImage = (url) => {
    return /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url);
  };

  const isVideo = (url) => {
    return /\.(mp4|webm|ogg|mov|avi|wmv)(\?.*)?$/i.test(url);
  };

  const getPlatformIcon = (platform) => {
    const platformLower = platform?.toLowerCase();
    switch (platformLower) {
      case "instagram":
        return <Instagram sx={{ fontSize: 16 }} />;
      case "facebook":
        return <Facebook sx={{ fontSize: 16 }} />;
      case "twitter":
      case "x":
        return <Twitter sx={{ fontSize: 16 }} />;
      case "linkedin":
        return <LinkedIn sx={{ fontSize: 16 }} />;
      case "youtube":
        return <YouTube sx={{ fontSize: 16 }} />;
      default:
        return <ShareIcon sx={{ fontSize: 16 }} />;
    }
  };

  const handleApprove = async () => {
    // Double-check time validation before approving
    if (!canApprove) {
      setError(
        validationMessage || "Cannot approve - insufficient time buffer"
      );
      return;
    }

    try {
      setLoading(true);
      setActionType("approve");
      setError(null);
      setSuccess(null);

      const clientId = getAuthTokenPayload()?.clientId;
      const scheduleDateTime =
        post.rawScheduleDateTime ||
        post.originalScheduleDateTime ||
        new Date(post.scheduledFor).toISOString();

      const result = await approvePost(scheduleDateTime, clientId);

      if (result.success) {
        setSuccess("✅ Post approved successfully!");
        const updatedPost = { ...post, status: "Approved" };
        if (onPostUpdated) {
          onPostUpdated(updatedPost);
        }
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(result.message || "Failed to approve post");
      }
    } catch (error) {
      console.error("Error approving post:", error);
      setError("Network error occurred while approving post");
    } finally {
      setLoading(false);
      setActionType(null);
    }
  };

  const handleReject = async () => {
    try {
      setLoading(true);
      setActionType("reject");
      setError(null);
      setSuccess(null);

      const clientId = getAuthTokenPayload()?.clientId;
      const scheduleDateTime =
        post.rawScheduleDateTime ||
        post.originalScheduleDateTime ||
        new Date(post.scheduledFor).toISOString();

      const result = await rejectPost(scheduleDateTime, clientId);

      if (result.success) {
        setSuccess("❌ Post rejected successfully!");
        const updatedPost = { ...post, status: "Rejected" };
        if (onPostUpdated) {
          onPostUpdated(updatedPost);
        }
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(result.message || "Failed to reject post");
      }
    } catch (error) {
      console.error("Error rejecting post:", error);
      setError("Network error occurred while rejecting post");
    } finally {
      setLoading(false);
      setActionType(null);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case "Approved":
        return {
          bgcolor: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
          color: "#065f46",
          borderColor: "#10b981",
          icon: <ApproveIcon />,
          glow: "0 0 20px rgba(16, 185, 129, 0.3)",
        };
      case "Rejected":
        return {
          bgcolor: "linear-gradient(135deg, #fee2e2 0%, #fca5a5 100%)",
          color: "#991b1b",
          borderColor: "#ef4444",
          icon: <DeclineIcon />,
          glow: "0 0 20px rgba(239, 68, 68, 0.3)",
        };
      case "Pending":
        return {
          bgcolor: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
          color: "#92400e",
          borderColor: "#f59e0b",
          icon: <TimeIcon />,
          glow: "0 0 20px rgba(245, 158, 11, 0.3)",
        };
      default:
        return {
          bgcolor: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
          color: "#475569",
          borderColor: "#64748b",
          icon: <SecurityIcon />,
          glow: "0 0 20px rgba(100, 116, 139, 0.3)",
        };
    }
  };

  const statusConfig = getStatusConfig(post.status);

  const nextImage = () => {
    setSelectedImageIndex((prev) =>
      prev === mediaUrls.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) =>
      prev === 0 ? mediaUrls.length - 1 : prev - 1
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(10px)",
          },
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 1, md: 2 },
        }}
      >
        <Fade in={open}>
          <Paper
            elevation={0}
            sx={{
              width: "95%",
              maxWidth: 1400,
              maxHeight: "95vh",
              overflow: "hidden",
              borderRadius: 6,
              outline: "none",
              background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
              position: "relative",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: `
                0 25px 50px -12px rgba(0, 0, 0, 0.25),
                0 0 0 1px rgba(255, 255, 255, 0.1),
                inset 0 1px 0 rgba(255, 255, 255, 0.2)
              `,
              animation: `${slideInUp} 0.5s ease-out`,
            }}
          >
            {/* Premium Loading Bar */}
            {loading && (
              <LinearProgress
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 100,
                  height: 3,
                  "& .MuiLinearProgress-bar": {
                    background:
                      "linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)",
                  },
                }}
              />
            )}

            {/* Luxurious Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                borderBottom: "1px solid rgba(148, 163, 184, 0.2)",
                backdropFilter: "blur(20px)",
                p: 4,
              }}
            >
              <Grid container alignItems="center" spacing={3}>
                <Grid item xs>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                    <Box
                      sx={{
                        position: "relative",
                        background:
                          "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                        borderRadius: 4,
                        p: 2,
                        boxShadow: "0 10px 25px rgba(59, 130, 246, 0.3)",
                      }}
                    >
                      <BusinessIcon sx={{ color: "white", fontSize: 32 }} />
                      <Box
                        sx={{
                          position: "absolute",
                          top: -2,
                          right: -2,
                          width: 12,
                          height: 12,
                          bgcolor: "#10b981",
                          borderRadius: "50%",
                          border: "2px solid white",
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 800,
                          background:
                            "linear-gradient(135deg, #1e293b 0%, #475569 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          fontSize: { xs: "1.5rem", md: "2rem" },
                          mb: 0.5,
                        }}
                      >
                        Premium Content Review
                      </Typography>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748b",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                          }}
                        >
                          ID: {post.id}
                        </Typography>

                        <Divider orientation="vertical" flexItem />

                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748b",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                          }}
                        >
                          {post.contentType || post.type}
                        </Typography>

                        <Chip
                          icon={<StarIcon sx={{ fontSize: 14 }} />}
                          label="Premium"
                          size="small"
                          sx={{
                            background:
                              "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                            color: "white",
                            fontWeight: 700,
                            fontSize: "0.7rem",
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                <Grid item>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Chip
                      icon={statusConfig.icon}
                      label={post.status}
                      sx={{
                        background: statusConfig.bgcolor,
                        color: statusConfig.color,
                        border: `2px solid ${statusConfig.borderColor}`,
                        fontWeight: 800,
                        fontSize: "1rem",
                        height: 44,
                        px: 3,
                        borderRadius: 6,
                        boxShadow: statusConfig.glow,
                        "& .MuiChip-icon": {
                          fontSize: 20,
                        },
                      }}
                    />

                    <Tooltip title="Close Review" placement="bottom">
                      <IconButton
                        onClick={onClose}
                        sx={{
                          background:
                            "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                          border: "1px solid rgba(148, 163, 184, 0.2)",
                          color: "#64748b",
                          width: 48,
                          height: 48,
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)",
                            transform: "scale(1.1) rotate(90deg)",
                            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
                          },
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Premium Content Area */}
            <Box sx={{ maxHeight: "calc(95vh - 250px)", overflow: "auto" }}>
              <Grid container sx={{ height: "100%" }}>
                {/* Left Panel - Enhanced Media & Preview */}
                <Grid item xs={12} md={7}>
                  <Box
                    sx={{
                      p: 4,
                      height: "100%",
                      background:
                        "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                    }}
                  >
                    {/* Premium Media Viewer */}
                    {mediaUrls.length > 0 ? (
                      <Card
                        elevation={0}
                        sx={{
                          position: "relative",
                          overflow: "hidden",
                          borderRadius: 4,
                          height: { xs: 350, md: 450 },
                          flexShrink: 0,
                          border: "1px solid rgba(148, 163, 184, 0.1)",
                          boxShadow: `
                            0 20px 25px -5px rgba(0, 0, 0, 0.1),
                            0 10px 10px -5px rgba(0, 0, 0, 0.04),
                            inset 0 1px 0 rgba(255, 255, 255, 0.1)
                          `,
                          background:
                            "linear-gradient(135deg, #000000 0%, #1f2937 100%)",
                        }}
                      >
                        {isImage(mediaUrls[selectedImageIndex]) ? (
                          <>
                            <CardMedia
                              component="img"
                              src={mediaUrls[selectedImageIndex]}
                              alt={`Media ${selectedImageIndex + 1}`}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  transform: "scale(1.02)",
                                },
                              }}
                              onClick={() => setShowFullImage(true)}
                            />

                            <Tooltip title="View Fullscreen">
                              <IconButton
                                onClick={() => setShowFullImage(true)}
                                sx={{
                                  position: "absolute",
                                  top: 16,
                                  right: 16,
                                  background: "rgba(0, 0, 0, 0.7)",
                                  backdropFilter: "blur(10px)",
                                  color: "white",
                                  "&:hover": {
                                    background: "rgba(0, 0, 0, 0.9)",
                                    transform: "scale(1.1)",
                                  },
                                }}
                              >
                                <FullscreenIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : isVideo(mediaUrls[selectedImageIndex]) ? (
                          <Box
                            sx={{
                              position: "relative",
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Box
                              component="video"
                              src={mediaUrls[selectedImageIndex]}
                              controls
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexDirection: "column",
                              gap: 2,
                              color: "white",
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 64, opacity: 0.5 }} />
                            <Typography sx={{ opacity: 0.7 }}>
                              {mediaUrls[selectedImageIndex].split("/").pop()}
                            </Typography>
                          </Box>
                        )}

                        {/* Enhanced Navigation */}
                        {mediaUrls.length > 1 && (
                          <>
                            <IconButton
                              onClick={prevImage}
                              sx={{
                                position: "absolute",
                                left: 16,
                                top: "50%",
                                transform: "translateY(-50%)",
                                background:
                                  "linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 100%)",
                                backdropFilter: "blur(10px)",
                                color: "white",
                                width: 48,
                                height: 48,
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 100%)",
                                  transform: "translateY(-50%) scale(1.1)",
                                },
                                transition: "all 0.3s ease",
                              }}
                            >
                              <ChevronLeftIcon />
                            </IconButton>

                            <IconButton
                              onClick={nextImage}
                              sx={{
                                position: "absolute",
                                right: 16,
                                top: "50%",
                                transform: "translateY(-50%)",
                                background:
                                  "linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 100%)",
                                backdropFilter: "blur(10px)",
                                color: "white",
                                width: 48,
                                height: 48,
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 100%)",
                                  transform: "translateY(-50%) scale(1.1)",
                                },
                                transition: "all 0.3s ease",
                              }}
                            >
                              <ChevronRightIcon />
                            </IconButton>

                            {/* Elegant Counter */}
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 20,
                                right: 20,
                                background:
                                  "linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 100%)",
                                backdropFilter: "blur(10px)",
                                color: "white",
                                px: 3,
                                py: 1,
                                borderRadius: 3,
                                fontSize: "0.875rem",
                                fontWeight: 700,
                                border: "1px solid rgba(255, 255, 255, 0.1)",
                              }}
                            >
                              {selectedImageIndex + 1} / {mediaUrls.length}
                            </Box>
                          </>
                        )}
                      </Card>
                    ) : (
                      <Card
                        sx={{
                          height: { xs: 300, md: 400 },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background:
                            "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                          border: "2px dashed #cbd5e1",
                          borderRadius: 4,
                          flexShrink: 0,
                        }}
                      >
                        <Box sx={{ textAlign: "center", color: "#64748b" }}>
                          <ImageIcon
                            sx={{ fontSize: 64, mb: 2, opacity: 0.5 }}
                          />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            No Media Files
                          </Typography>
                        </Box>
                      </Card>
                    )}

                    {/* Premium Thumbnail Gallery */}
                    {mediaUrls.length > 1 && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          overflowX: "auto",
                          pb: 2,
                          flexShrink: 0,
                          "&::-webkit-scrollbar": {
                            height: 6,
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "rgba(148, 163, 184, 0.1)",
                            borderRadius: 3,
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background:
                              "linear-gradient(90deg, #3b82f6, #8b5cf6)",
                            borderRadius: 3,
                          },
                        }}
                      >
                        {mediaUrls.map((url, index) => (
                          <Box
                            key={index}
                            onClick={() => setSelectedImageIndex(index)}
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: 3,
                              overflow: "hidden",
                              border:
                                selectedImageIndex === index
                                  ? "3px solid #3b82f6"
                                  : "2px solid rgba(148, 163, 184, 0.2)",
                              cursor: "pointer",
                              flexShrink: 0,
                              position: "relative",
                              transition: "all 0.3s ease",
                              boxShadow:
                                selectedImageIndex === index
                                  ? "0 10px 25px rgba(59, 130, 246, 0.3)"
                                  : "0 4px 6px rgba(0, 0, 0, 0.1)",
                              "&:hover": {
                                transform: "scale(1.05) translateY(-2px)",
                                borderColor: "#3b82f6",
                                boxShadow:
                                  "0 10px 25px rgba(59, 130, 246, 0.3)",
                              },
                            }}
                          >
                            {isImage(url) ? (
                              <Box
                                component="img"
                                src={url}
                                alt={`Thumb ${index + 1}`}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  background:
                                    "linear-gradient(135deg, #374151 0%, #1f2937 100%)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <VideoIcon
                                  sx={{ color: "#9ca3af", fontSize: 28 }}
                                />
                              </Box>
                            )}

                            {selectedImageIndex === index && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 4,
                                  right: 4,
                                  width: 12,
                                  height: 12,
                                  bgcolor: "#3b82f6",
                                  borderRadius: "50%",
                                  border: "2px solid white",
                                  animation: `${pulseGlow} 2s infinite`,
                                }}
                              />
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Enhanced Social Media Preview */}
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 4,
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid rgba(148, 163, 184, 0.1)",
                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                        background:
                          "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                      }}
                    >
                      <CardContent sx={{ p: 4, flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#374151",
                            mb: 3,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              background:
                                "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                              borderRadius: 2,
                              p: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <VisibilityIcon
                              sx={{ color: "white", fontSize: 20 }}
                            />
                          </Box>
                          Social Media Preview
                        </Typography>

                        {/* Premium Mock Post */}
                        <Box
                          sx={{
                            background:
                              "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
                            border: "1px solid rgba(148, 163, 184, 0.15)",
                            borderRadius: 3,
                            p: 3,
                            flex: 1,
                            boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.05)",
                          }}
                        >
                          {/* Enhanced Post Header */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 3,
                            }}
                          >
                            <Badge
                              overlap="circular"
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                              }}
                              badgeContent={
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    bgcolor: "#10b981",
                                    borderRadius: "50%",
                                    border: "2px solid white",
                                  }}
                                />
                              }
                            >
                              <Avatar
                                sx={{
                                  width: 48,
                                  height: 48,
                                  background:
                                    "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                                  fontSize: "1.2rem",
                                  fontWeight: 700,
                                  boxShadow:
                                    "0 4px 14px rgba(59, 130, 246, 0.3)",
                                }}
                              >
                                B
                              </Avatar>
                            </Badge>

                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  color: "#374151",
                                  mb: 0.5,
                                }}
                              >
                                Brand Account
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#64748b",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <TimeIcon sx={{ fontSize: 12 }} />
                                {post.scheduledFor}
                              </Typography>
                            </Box>

                            <Stack direction="row" spacing={1}>
                              {post.platforms?.map((platform, index) => (
                                <Tooltip key={index} title={platform}>
                                  <Chip
                                    icon={getPlatformIcon(platform)}
                                    label={platform}
                                    size="small"
                                    sx={{
                                      background:
                                        "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                                      color: "white",
                                      fontSize: "0.75rem",
                                      height: 28,
                                      fontWeight: 600,
                                      "& .MuiChip-icon": {
                                        color: "white",
                                      },
                                    }}
                                  />
                                </Tooltip>
                              ))}
                            </Stack>
                          </Box>

                          {/* Enhanced Content */}
                          <Typography
                            variant="body1"
                            sx={{
                              color: "#374151",
                              lineHeight: 1.6,
                              mb: 3,
                              whiteSpace: "pre-wrap",
                              fontSize: "1rem",
                            }}
                          >
                            {post.content}
                          </Typography>

                          {/* Premium Media Preview */}
                          {mediaUrls.length > 0 && (
                            <Box
                              sx={{
                                borderRadius: 3,
                                overflow: "hidden",
                                mb: 3,
                                border: "1px solid rgba(148, 163, 184, 0.15)",
                                position: "relative",
                              }}
                            >
                              {isImage(mediaUrls[0]) ? (
                                <Box
                                  component="img"
                                  src={mediaUrls[0]}
                                  alt="Post preview"
                                  sx={{
                                    width: "100%",
                                    maxHeight: 160,
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    height: 120,
                                    background:
                                      "linear-gradient(135deg, #374151 0%, #1f2937 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "relative",
                                  }}
                                >
                                  <PlayArrowIcon
                                    sx={{ color: "white", fontSize: 48 }}
                                  />
                                </Box>
                              )}

                              {mediaUrls.length > 1 && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    background: "rgba(0, 0, 0, 0.7)",
                                    color: "white",
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 2,
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                  }}
                                >
                                  +{mediaUrls.length - 1} more
                                </Box>
                              )}
                            </Box>
                          )}

                          {/* Premium Engagement Bar */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              pt: 2,
                              borderTop: "1px solid rgba(148, 163, 184, 0.1)",
                            }}
                          >
                            <Stack direction="row" spacing={4}>
                              {[
                                {
                                  icon: FavoriteIcon,
                                  label: "Like",
                                  color: "#ef4444",
                                },
                                {
                                  icon: CommentIcon,
                                  label: "Comment",
                                  color: "#3b82f6",
                                },
                                {
                                  icon: ShareOutlinedIcon,
                                  label: "Share",
                                  color: "#10b981",
                                },
                              ].map(({ icon: Icon, label, color }) => (
                                <Box
                                  key={label}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      color: color,
                                      transform: "translateY(-1px)",
                                    },
                                  }}
                                >
                                  <Icon
                                    sx={{ fontSize: 18, color: "#64748b" }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#64748b",
                                      fontWeight: 600,
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    {label}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <TrendingUpIcon
                                sx={{ fontSize: 16, color: "#10b981" }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ color: "#10b981", fontWeight: 600 }}
                              >
                                Estimated Reach: 2.5K
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                </Grid>

                {/* Right Panel - Enhanced Details */}
                <Grid item xs={12} md={5}>
                  <Box sx={{ p: 4, height: "100%" }}>
                    {/* Enhanced Post Content Card */}
                    <Card
                      elevation={0}
                      sx={{
                        mb: 3,
                        borderRadius: 4,
                        border: "1px solid rgba(148, 163, 184, 0.1)",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                        background:
                          "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#374151",
                            mb: 3,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              background:
                                "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                              borderRadius: 2,
                              p: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <TagIcon sx={{ color: "white", fontSize: 20 }} />
                          </Box>
                          Post Content
                        </Typography>

                        <Box
                          sx={{
                            background:
                              "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
                            border: "1px solid rgba(148, 163, 184, 0.15)",
                            borderRadius: 3,
                            p: 3,
                            mb: 3,
                            position: "relative",
                            overflow: "hidden",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: 2,
                              background:
                                "linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)",
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              color: "#1f2937",
                              lineHeight: 1.7,
                              fontSize: "1rem",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {post.content}
                          </Typography>
                        </Box>

                        {/* Enhanced Hashtags */}
                        {post.hashtags && post.hashtags.length > 0 && (
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: "#64748b",
                                mb: 2,
                                textTransform: "uppercase",
                                letterSpacing: 1,
                              }}
                            >
                              Hashtags
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                flexWrap: "wrap",
                              }}
                            >
                              {post.hashtags.map((hashtag, index) => (
                                <Chip
                                  key={index}
                                  label={hashtag}
                                  size="small"
                                  sx={{
                                    background:
                                      "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
                                    color: "#1e40af",
                                    fontWeight: 700,
                                    border: "1px solid rgba(59, 130, 246, 0.2)",
                                    "&:hover": {
                                      background:
                                        "linear-gradient(135deg, #bfdbfe 0%, #93c5fd 100%)",
                                      transform: "translateY(-1px)",
                                      boxShadow:
                                        "0 4px 12px rgba(59, 130, 246, 0.3)",
                                    },
                                    transition: "all 0.2s ease",
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>

                    {/* Enhanced Scheduling Details Card */}
                    <Card
                      elevation={0}
                      sx={{
                        mb: 3,
                        borderRadius: 4,
                        border: "1px solid rgba(148, 163, 184, 0.1)",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                        background:
                          "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#374151",
                            mb: 3,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              background:
                                "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                              borderRadius: 2,
                              p: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <ScheduleIcon
                              sx={{ color: "white", fontSize: 20 }}
                            />
                          </Box>
                          Scheduling Details
                        </Typography>

                        <Stack spacing={3}>
                          {/* Enhanced Platforms */}
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: "#64748b",
                                mb: 2,
                                textTransform: "uppercase",
                                letterSpacing: 1,
                              }}
                            >
                              Platforms
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={1.5}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              {post.platforms?.map((platform, index) => (
                                <Chip
                                  key={index}
                                  icon={getPlatformIcon(platform)}
                                  label={platform}
                                  sx={{
                                    background:
                                      "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
                                    color: "#065f46",
                                    fontWeight: 700,
                                    border: "1px solid rgba(16, 185, 129, 0.2)",
                                    borderRadius: 3,
                                    "& .MuiChip-icon": {
                                      color: "#065f46",
                                    },
                                  }}
                                />
                              ))}
                            </Stack>
                            {post.accountNames && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#374151",
                                  mt: 2,
                                  fontWeight: 600,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <BusinessIcon
                                  sx={{ fontSize: 16, color: "#64748b" }}
                                />
                                {post.accountNames}
                              </Typography>
                            )}
                          </Box>

                          {/* Enhanced Schedule Time */}
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: "#64748b",
                                mb: 2,
                                textTransform: "uppercase",
                                letterSpacing: 1,
                              }}
                            >
                              Scheduled For
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                p: 3,
                                background:
                                  "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                                borderRadius: 3,
                                border: "1px solid rgba(245, 158, 11, 0.3)",
                              }}
                            >
                              <Box
                                sx={{
                                  background:
                                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                  borderRadius: 2,
                                  p: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <TimeIcon
                                  sx={{ color: "white", fontSize: 20 }}
                                />
                              </Box>
                              <Typography
                                variant="body1"
                                sx={{ color: "#92400e", fontWeight: 700 }}
                              >
                                {post.scheduledFor}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Enhanced Requested By */}
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: "#64748b",
                                mb: 2,
                                textTransform: "uppercase",
                                letterSpacing: 1,
                              }}
                            >
                              Requested By
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                p: 3,
                                background:
                                  "linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)",
                                borderRadius: 3,
                                border: "1px solid rgba(99, 102, 241, 0.3)",
                              }}
                            >
                              <Avatar
                                sx={{
                                  background:
                                    "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                                  width: 40,
                                  height: 40,
                                }}
                              >
                                <PersonIcon sx={{ color: "white" }} />
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="body1"
                                  sx={{ color: "#4338ca", fontWeight: 700 }}
                                >
                                  {post.requestedBy}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#64748b", fontWeight: 600 }}
                                >
                                  {post.requestedAt}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>

                    {/* ADDED: Time Validation Alert - Show when time buffer is insufficient */}
                    {userPermissions.write &&
                      post.status === "Pending" &&
                      !canApprove && (
                        <Alert
                          severity="error"
                          icon={<TimeIcon />}
                          sx={{
                            mb: 3,
                            borderRadius: 3,
                            border: "2px solid #ef4444",
                            background:
                              "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
                            "& .MuiAlert-message": { fontWeight: 600 },
                            boxShadow: "0 8px 20px rgba(239, 68, 68, 0.3)",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 800, color: "#991b1b", mb: 1 }}
                          >
                            ⚠️ Cannot Approve - Time Buffer Insufficient
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#7f1d1d", fontWeight: 600 }}
                          >
                            {validationMessage ||
                              "Due to approval time crossed, you cannot approve this post. The scheduled time must be at least 10 minutes in the future."}
                          </Typography>
                        </Alert>
                      )}

                    {/* Enhanced Messages */}
                    {error && (
                      <Alert
                        severity="error"
                        sx={{
                          mb: 2,
                          borderRadius: 3,
                          border: "1px solid rgba(239, 68, 68, 0.2)",
                          "& .MuiAlert-message": { fontWeight: 600 },
                        }}
                      >
                        {error}
                      </Alert>
                    )}

                    {success && (
                      <Alert
                        severity="success"
                        sx={{
                          mb: 2,
                          borderRadius: 3,
                          border: "1px solid rgba(34, 197, 94, 0.2)",
                          "& .MuiAlert-message": { fontWeight: 600 },
                        }}
                      >
                        {success}
                      </Alert>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* UPDATED: Premium Action Footer - Only show if time is valid */}
            {userPermissions.write &&
              post.status === "Pending" &&
              canApprove && (
                <Box
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                    borderTop: "1px solid rgba(148, 163, 184, 0.2)",
                    backdropFilter: "blur(20px)",
                    p: 4,
                    zIndex: 10,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 4,
                      maxWidth: 500,
                      mx: "auto",
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={
                        loading && actionType === "reject" ? (
                          <CircularProgress size={24} />
                        ) : (
                          <DeclineIcon />
                        )
                      }
                      onClick={handleReject}
                      disabled={loading}
                      sx={{
                        flex: 1,
                        height: 64,
                        borderWidth: 2,
                        borderColor: "#ef4444",
                        color: "#ef4444",
                        fontWeight: 800,
                        fontSize: "1.1rem",
                        borderRadius: 4,
                        textTransform: "none",
                        "&:hover": {
                          borderWidth: 2,
                          borderColor: "#dc2626",
                          background:
                            "linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(239, 68, 68, 0.1) 100%)",
                          transform: "translateY(-3px)",
                          boxShadow: "0 12px 30px rgba(239, 68, 68, 0.4)",
                        },
                        "&:disabled": {
                          opacity: 0.6,
                        },
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      {loading && actionType === "reject"
                        ? "Rejecting..."
                        : "Reject Post"}
                    </Button>

                    <Button
                      variant="contained"
                      size="large"
                      startIcon={
                        loading && actionType === "approve" ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          <ApproveIcon />
                        )
                      }
                      onClick={handleApprove}
                      disabled={loading}
                      sx={{
                        flex: 1,
                        height: 64,
                        background:
                          "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        fontWeight: 800,
                        fontSize: "1.1rem",
                        borderRadius: 4,
                        textTransform: "none",
                        boxShadow: "0 8px 20px rgba(16, 185, 129, 0.4)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #059669 0%, #047857 100%)",
                          transform: "translateY(-3px)",
                          boxShadow: "0 12px 30px rgba(16, 185, 129, 0.5)",
                        },
                        "&:disabled": {
                          opacity: 0.6,
                        },
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      {loading && actionType === "approve"
                        ? "Approving..."
                        : "Approve Post"}
                    </Button>
                  </Box>
                </Box>
              )}

            {/* ADDED: Show message when buttons are hidden due to time */}
            {userPermissions.write &&
              post.status === "Pending" &&
              !canApprove && (
                <Box
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    background:
                      "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
                    borderTop: "2px solid #ef4444",
                    backdropFilter: "blur(20px)",
                    p: 3,
                    zIndex: 10,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#991b1b",
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                    }}
                  >
                    <TimeIcon sx={{ color: "#dc2626" }} />
                    Approval actions are disabled - insufficient time buffer
                  </Typography>
                </Box>
              )}
          </Paper>
        </Fade>
      </Modal>

      {/* Fullscreen Image Modal */}
      <Modal
        open={showFullImage}
        onClose={() => setShowFullImage(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Fade in={showFullImage}>
          <Box
            sx={{
              maxWidth: "95vw",
              maxHeight: "95vh",
              outline: "none",
              position: "relative",
            }}
          >
            <IconButton
              onClick={() => setShowFullImage(false)}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 1,
                background: "rgba(0, 0, 0, 0.7)",
                color: "white",
                "&:hover": {
                  background: "rgba(0, 0, 0, 0.9)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            {mediaUrls[selectedImageIndex] &&
              isImage(mediaUrls[selectedImageIndex]) && (
                <Box
                  component="img"
                  src={mediaUrls[selectedImageIndex]}
                  alt="Full size preview"
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: 2,
                  }}
                />
              )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default PostPreviewModal;
